import { useEffect, useMemo, useState } from 'react';
import { api, ago, money, pct, price, type ModeState, type Opportunity, type Settings } from '../lib/api.ts';
import { ExecuteModal } from '../components/ExecuteModal.tsx';
import { RecentOpportunities } from '../components/RecentOpportunities.tsx';

/**
 * Live opportunities screen (FR-4.2).
 *
 * The list arrives over the WebSocket and is replaced wholesale, so a row can
 * never linger after the opportunity behind it has closed. Rows that have
 * passed their expiry are dimmed and their Execute button disabled — an owner
 * must not be able to click Execute on a price that no longer exists.
 *
 * Underneath sits the recently-detected panel. The live list above is empty
 * almost all of the time — that is the honest state of the market, but on its
 * own it reads as a broken tool, so the record of what just closed belongs on
 * the same screen.
 */

interface Props {
  opportunities: Opportunity[];
  settings: Settings | null;
  quoteCurrency: string;
  connected: boolean;
  mode: ModeState | null;
}

type SortKey = 'netPct' | 'symbol' | 'detectedAt';

export function Opportunities({ opportunities, settings, quoteCurrency, connected, mode }: Props) {
  const isTransfer = mode?.mode === 'transfer';
  // Watch rows exist only when a watch level is configured below the trade bar.
  const watching = isTransfer && (mode?.transfer?.watchPct ?? 0) > 0;
  const holdSeconds = isTransfer ? (mode?.transfer?.minHoldSeconds ?? 0) : 0;
  const [minNetPct, setMinNetPct] = useState(0);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('netPct');
  const [selected, setSelected] = useState<Opportunity | null>(null);

  /**
   * The modal must follow the LIVE row, not the copy taken when it was clicked.
   *
   * setSelected froze an Opportunity at click time. Its expiresAt is two
   * seconds after the tick it was built from, so by the time anyone had read
   * the dialog it declared itself expired and disabled its own Confirm button
   * — permanently, because nothing in the frozen copy ever moved again. The
   * row behind it was being re-priced the whole time and was perfectly
   * takeable. No trade could ever be confirmed through this dialog.
   *
   * Re-resolving by id each render keeps the price, the net and the countdown
   * current. When the row genuinely closes it drops out of the live list and
   * the frozen copy takes over, which is exactly when "expired" is the truth.
   */
  const selectedLive = selected
    ? (opportunities.find((o) => o.id === selected.id) ?? selected)
    : null;
  const [now, setNow] = useState(Date.now());

  // Expiry is a function of wall-clock time, not of new data — without a
  // ticking clock a stale row would keep looking actionable while the feed is
  // quiet, which is precisely the case where it is most dangerous.
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(timer);
  }, []);

  const exchanges = useMemo(() => {
    const set = new Set<string>();
    for (const o of opportunities) {
      set.add(o.buyExchange);
      set.add(o.sellExchange);
    }
    return [...set].sort();
  }, [opportunities]);

  const visible = useMemo(() => {
    const filtered = opportunities.filter((o) => {
      if (o.netPct * 100 < minNetPct) return false;
      if (symbolFilter && !o.symbol.toLowerCase().includes(symbolFilter.toLowerCase())) return false;
      if (exchangeFilter && o.buyExchange !== exchangeFilter && o.sellExchange !== exchangeFilter) {
        return false;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      if (sortKey === 'symbol') return a.symbol.localeCompare(b.symbol);
      if (sortKey === 'detectedAt') return b.detectedAt - a.detectedAt;
      return b.netPct - a.netPct;
    });
  }, [opportunities, minNetPct, symbolFilter, exchangeFilter, sortKey]);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Live opportunities</h1>
          <div className="sub">
            {isTransfer
              ? 'Net of both taker fees AND the flat withdrawal fee. The sell price can still move while the coin is in transit.'
              : 'Net of both taker fees and the safety buffer. Nothing here is a headline gap.'}
            {watching && (
              <>
                <br />
                Rows from {mode!.transfer!.watchPct}% up are shown so the screen reflects what the
                engine is seeing; only those at {mode!.transfer!.minNetPct}% or better can be traded.
              </>
            )}
            {holdSeconds > 0 && (
              <>
                <br />
                Each row stays for at least {holdSeconds}s and is re-priced the whole time. It stops
                being executable the moment the gap drops below the bar or the price goes stale — so
                a row you can still see is not necessarily a row you can still take.
              </>
            )}
          </div>
        </div>
        <div className="sub">
          {connected ? (
            <span className="green">
              <span className="dot live" />
              streaming
            </span>
          ) : (
            <span className="amber">
              <span className="dot reconnecting" />
              reconnecting…
            </span>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="row" style={{ flex: 1 }}>
            <div style={{ width: 130 }}>
              <label>Min net %</label>
              <input
                type="number"
                step="0.05"
                value={minNetPct}
                onChange={(e) => setMinNetPct(Number(e.target.value))}
              />
            </div>
            <div style={{ width: 150 }}>
              <label>Pair</label>
              <input
                type="text"
                placeholder="BTC"
                value={symbolFilter}
                onChange={(e) => setSymbolFilter(e.target.value)}
              />
            </div>
            <div style={{ width: 150 }}>
              <label>Exchange</label>
              <select value={exchangeFilter} onChange={(e) => setExchangeFilter(e.target.value)}>
                <option value="">any</option>
                {exchanges.map((ex) => (
                  <option key={ex} value={ex}>
                    {ex}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ width: 150 }}>
              <label>Sort by</label>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SortKey)}>
                <option value="netPct">net profit</option>
                <option value="detectedAt">newest</option>
                <option value="symbol">pair</option>
              </select>
            </div>
          </div>
          <div className="dim" style={{ fontSize: 13 }}>
            {visible.length} of {opportunities.length}
            {watching && (
              <>
                {' · '}
                <span className="green">
                  {visible.filter((o) => o.tradeable !== false && now < o.expiresAt).length} tradeable
                </span>
              </>
            )}
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="empty">
            <strong>No opportunities above the threshold right now.</strong>
            {isTransfer && mode?.transfer && (
              <>
                Transfer mode surfaces gaps at {mode.transfer.minNetPct}% net or better, after both
                taker fees and the flat withdrawal fee.
                <br />
              </>
            )}
            {settings && (
              <>
                Threshold is {pct(settings.thresholdPct, 2)} net, after a {pct(settings.bufferPct, 2)}{' '}
                buffer.
                <br />
                An empty list here is the normal state — spreads that clear both taker fees are rare
                and short-lived. What the detector caught earlier is listed below.
              </>
            )}
          </div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Pair</th>
                  <th>Buy on</th>
                  <th className="num">Buy price</th>
                  <th>Sell on</th>
                  <th className="num">Sell price</th>
                  <th className="num">Gross</th>
                  <th className="num">Net</th>
                  {isTransfer && <th className="num">Fee drag</th>}
                  {isTransfer && <th className="num">Min size</th>}
                  {isTransfer && <th className="num">Transit</th>}
                  <th className="num">Est. profit</th>
                  <th className="num">Age</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((o) => {
                  // Three reasons a row may not be actionable, and they are not
                  // the same thing: the quote has gone stale, or the gap itself
                  // has fallen below the bar. Both stay on screen while held.
                  const stale = now >= o.expiresAt;
                  // Undefined means a payload from before watch rows existed;
                  // those were all tradeable.
                  const belowBar = o.tradeable === false;
                  const actionable = !stale && !belowBar;
                  const opacity = stale ? 0.4 : belowBar ? 0.62 : undefined;
                  return (
                    <tr key={o.id} style={opacity === undefined ? undefined : { opacity }}>
                      <td className="sym">
                        {o.symbol}
                        {belowBar && (
                          <span
                            className={`pill ${o.blockedReason ? 'warn' : ''}`}
                            style={{ marginLeft: 8 }}
                            title={
                              o.blockedReason ??
                              `below the ${mode?.transfer?.minNetPct ?? ''}% trade bar — shown, not tradeable`
                            }
                          >
                            {o.blockedReason ? 'no address' : 'watch'}
                          </span>
                        )}
                        {!belowBar && stale && (
                          <span
                            className="pill warn"
                            style={{ marginLeft: 8 }}
                            title="no fresh price in the last couple of seconds — held on screen, but nothing may be executed on a stale quote"
                          >
                            stale
                          </span>
                        )}
                      </td>
                      <td>{o.buyExchange}</td>
                      <td className="num">{price(o.buyPrice)}</td>
                      <td>{o.sellExchange}</td>
                      <td className="num">{price(o.sellPrice)}</td>
                      <td className="num dim">{pct(o.grossPct)}</td>
                      <td className={`num ${actionable ? 'green' : 'dim'}`}>
                        <strong>{pct(o.netPct)}</strong>
                      </td>
                      {isTransfer && (
                        <td className="num amber" title="Flat withdrawal fee as a share of this trade">
                          {o.transfer ? pct(o.transfer.withdrawalDragPct, 2) : '—'}
                        </td>
                      )}
                      {isTransfer && (
                        <td
                          className="num faint"
                          title="Below this notional the flat withdrawal fee eats the gap"
                        >
                          {o.transfer && Number.isFinite(o.transfer.minimumViableSize)
                            ? Math.ceil(o.transfer.minimumViableSize)
                            : '—'}
                        </td>
                      )}
                      {isTransfer && (
                        <td className="num faint" title="Typical on-chain confirmation time">
                          {o.transfer ? `~${o.transfer.estimatedMinutes}m` : '—'}
                        </td>
                      )}
                      <td className="num">
                        {money(o.netProfitQuote)} {quoteCurrency}
                      </td>
                      <td className="num faint">{ago(o.detectedAt, now)}</td>
                      <td>
                        <button
                          className="btn exec small"
                          disabled={!actionable}
                          onClick={() => setSelected(o)}
                          title={
                            o.blockedReason
                              ? o.blockedReason
                              : belowBar
                                ? `below the ${mode?.transfer?.minNetPct ?? ''}% trade bar — gaps this size usually do not survive the transfer`
                                : stale
                                  ? 'price is stale — no longer executable'
                                  : 'review and confirm'
                          }
                        >
                          {o.blockedReason ? 'no address' : belowBar ? 'below bar' : stale ? 'stale' : 'Execute'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RecentOpportunities
        quoteCurrency={quoteCurrency}
        liveCount={opportunities.length}
        minNetPct={mode?.transfer?.minNetPct ?? null}
      />

      {selectedLive && settings && (
        <ExecuteModal
          opportunity={selectedLive}
          settings={settings}
          quoteCurrency={quoteCurrency}
          onClose={() => setSelected(null)}
          onConfirm={(size) => api.execute(selectedLive.id, size)}
        />
      )}
    </>
  );
}
